import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  CASE_DEFAULT_DURATION_DAYS,
  CaseView,
  CodingModel,
  caseCoversDate,
  codingOf,
  defaultCaseExpiry,
  defaultCaseStart,
  endOfDayIso,
  startOfDayIso,
  toDateInput,
} from '@lib/guarantees/data-access';
import { createCase, updateCase } from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { selectAssignableDiagnoses } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { CalloutComponent } from '@lib/guarantees/feature/components/common/callout.component';
import { CodingChipComponent } from '@lib/guarantees/feature/components/common/coding-chip.component';
import { ControlDirective } from '@lib/guarantees/feature/components/common/control.directive';
import { FieldLabelComponent } from '@lib/guarantees/feature/components/common/field-label.component';
import { Icd10PickerComponent } from '@lib/guarantees/feature/components/icd10-picker/icd10-picker.component';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';

import { BUTTON_CLASSES, DIALOG_CLASSES, FIELD_MESSAGE_CLASS } from '../components/ui/ui.classes';

export interface CaseFormData {
  /** The case being amended. Absent, the form opens a new one. */
  view?: CaseView;
  /**
   * The care the case is being opened for — a guarantee's appointment date, or when it was raised.
   * The span is seeded to cover it, so a case opened from a visit three months back holds it.
   */
  anchorDate?: string;
  /** Files a guarantee's diagnosis under the case the moment it exists. Creation only. */
  assign?: { guaranteeId: string; diagnosisId: string };
}

/**
 * Opening and amending a case, asked the same way both times.
 *
 * A named front door, as the other operations have. Editing is deliberately not offered inside the
 * panel row: the row is where a case is read, and a form unfolding in it turns reading into editing
 * by accident. The dialog is the whole case at once — name, span and diagnoses — because they are
 * written as one, and a form that could only reach half of them would leave the rest to be found.
 */
export function openCaseForm(dialog: MatDialog, store: Store, data: CaseFormData = {}): void {
  dialog
    .open<CaseFormComponent, CaseFormData, CaseFormResult>(CaseFormComponent, {
      width: '90vw',
      maxWidth: '560px',
      maxHeight: '90vh',
      autoFocus: false,
      ariaLabelledBy: 'case-form-title',
      data,
    })
    .afterClosed()
    .pipe(take(1))
    .subscribe(result => {
      if (!result) {
        return;
      }

      const request = { code: result.name, startedAt: result.startedAt, expiresAt: result.expiresAt, diagnoses: result.diagnoses };

      store.dispatch(data.view ? updateCase({ caseId: data.view.case.id, request }) : createCase({ request, assign: data.assign }));
    });
}

/** What the form answers with. Both dates are full instants; the day they name is a whole day. */
interface CaseFormResult {
  name: string;
  startedAt: string;
  expiresAt: string;
  diagnoses: CodingModel[];
}

/**
 * A case as one form: what it is called, the span it runs for, and the diagnoses it is about.
 *
 * The span is the load-bearing field. It is what admits a guarantee — only one dated inside it may
 * be filed here — so it opens on a month around the care being filed rather than on today, and both
 * ends are movable: a case is often opened after the fact, over history that already exists.
 */
@Component({
  selector: 'guarantees-case-form',
  standalone: true,
  imports: [FormsModule, MatIcon, TranslatePipe, CalloutComponent, CodingChipComponent, ControlDirective, FieldLabelComponent, Icd10PickerComponent],
  templateUrl: './case-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaseFormComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;
  protected readonly buttons = BUTTON_CLASSES;
  protected readonly fieldMessage = FIELD_MESSAGE_CLASS;

  /** Stated on the form rather than asked for: every case runs the same span unless it is moved. */
  protected readonly duration = CASE_DEFAULT_DURATION_DAYS;

  protected readonly data = inject<CaseFormData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<CaseFormComponent, CaseFormResult>);
  private readonly store = inject(Store);

  /** Every diagnosis in the loaded window, so the member's own codes are offered before the book. */
  private readonly assignable = this.store.selectSignal(selectAssignableDiagnoses);

  protected readonly editing = !!this.data.view;

  protected readonly name = signal(this.data.view?.case.code ?? '');

  protected readonly startDate = signal(toDateInput(this.data.view?.case.startedAt ?? defaultCaseStart(this.data.anchorDate)));

  protected readonly endDate = signal(toDateInput(this.data.view?.case.expiresAt ?? defaultCaseExpiry(this.data.anchorDate)));

  protected readonly diagnoses = signal<CodingModel[]>(seedDiagnoses(this.data.view));

  /** What the draft already names, so the dictionary cannot offer one of its codes a second time. */
  protected readonly codes = computed(() => this.diagnoses().map(coding => coding.code ?? ''));

  /** The member's own codes this case does not already carry, first occurrence of each. */
  protected readonly suggestions = computed(() => {
    const taken = this.codes();
    const byCode = new Map<string, CodingModel>();

    for (const entry of this.assignable()) {
      const coding = codingOf(entry.diagnosis);
      if (coding?.code && !taken.includes(coding.code) && !byCode.has(coding.code)) {
        byCode.set(coding.code, coding);
      }
    }

    return [...byCode.values()];
  });

  protected readonly periodMissing = computed(() => !this.startDate() || !this.endDate());

  /**
   * Compared as text, which the bare `yyyy-MM-dd` a date input holds allows — no offset to disagree
   * over, so spelling order is calendar order.
   */
  protected readonly periodInverted = computed(() => !!this.startDate() && !!this.endDate() && this.endDate() < this.startDate());

  /**
   * Guarantees already filed here that the span being typed would no longer admit. Shrinking a span
   * does not unfile anything — the record keeps what a manager put in it — so this is said rather
   * than refused, before the write instead of discovered afterwards.
   */
  protected readonly strandedCount = computed(() => {
    const view = this.data.view;
    if (!view || this.periodMissing() || this.periodInverted()) {
      return 0;
    }

    const span = { ...view.case, startedAt: startOfDayIso(this.startDate()), expiresAt: endOfDayIso(this.endDate()) };

    return view.guarantees.filter(entry => !caseCoversDate(span, entry.visitDate)).length;
  });

  protected readonly canSubmit = computed(() => this.name().trim().length > 0 && this.diagnoses().length > 0 && !this.periodMissing() && !this.periodInverted());

  protected addCoding(coding: CodingModel): void {
    if (!coding.code) {
      return;
    }

    this.diagnoses.update(codings => (codings.some(entry => entry.code === coding.code) ? codings : [...codings, coding]));
  }

  protected removeCoding(coding: CodingModel): void {
    this.diagnoses.update(codings => codings.filter(entry => entry.code !== coding.code));
  }

  /**
   * The day is widened to its bounds, as everywhere a date field stands for a whole day: a span
   * ending at midnight would close before the last day it names, and one opening at the moment of
   * submission would refuse care given earlier that same day.
   */
  protected submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.dialogRef.close({
      name: this.name().trim(),
      startedAt: startOfDayIso(this.startDate()),
      expiresAt: endOfDayIso(this.endDate()),
      diagnoses: this.diagnoses(),
    });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}

/**
 * The codes a stored case already carries, flattened out of `{ icd10: … }` and deduplicated. The
 * stored set accumulated before it was curated in one place, so it can name one code twice — which
 * would render as two identical chips and be written back as two identical entries.
 */
function seedDiagnoses(view?: CaseView): CodingModel[] {
  const byCode = new Map<string, CodingModel>();

  for (const entry of view?.case.diagnoses ?? []) {
    if (entry.icd10?.code && !byCode.has(entry.icd10.code)) {
      byCode.set(entry.icd10.code, entry.icd10);
    }
  }

  return [...byCode.values()];
}
